import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api-client.config';
import { queryKeys } from '../react-query.config';
import { toast } from 'sonner';

interface ManualScore {
  periodCode: string;
  rowTeamScore: number;
  colTeamScore: number;
}

interface SubmitManualScoresRequest {
  contestId: string;
  scores: ManualScore[];
}

interface SubmitManualScoresResponse {
  success: boolean;
  message: string;
  questionId: string;
  periodsGraded: number;
  failedPeriods?: Array<{ periodCode: string; error: string }>;
  periods: Array<{
    periodCode: string;
    winningSquareNumber: number;
    rowTeamScore: number;
    colTeamScore: number;
    rowLastDigit: number;
    colLastDigit: number;
  }>;
}

export async function submitManualScores(
  data: SubmitManualScoresRequest
): Promise<SubmitManualScoresResponse> {
  const response = await (apiClient as any).post(
    `/api/admin/squares/${data.contestId}/manual-scores`,
    {
      scores: data.scores,
    }
  );
  return response as SubmitManualScoresResponse;
}

export function useSubmitManualScoresMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: submitManualScores,
    onSuccess: data => {
      if (data.success) {
        toast.success(data.message, {
          description: `Graded ${data.periodsGraded} periods.`,
        });
      } else {
        toast.error(data.message, {
          description: data.failedPeriods?.length
            ? `Failed: ${data.failedPeriods.map(f => `${f.periodCode}`).join(', ')}`
            : `Some periods failed.`,
          duration: 6000,
        });
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.squares.detail(data.questionId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.squares.periodResults(data.questionId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.squares.entries(data.questionId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.squares.all });
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'An error occurred while saving scores';
      const details = error?.response?.data?.details || '';
      toast.error('Failed to save scores', {
        description: details ? `${errorMessage}: ${details}` : errorMessage,
        duration: 5000,
      });
      console.error('Manual scoring error:', error);
    },
  });
}
